import React, { useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";
import useSiteContent from "../lib/useSiteContent";

export default function ContactPage() {
  const { t, lang } = useLang();
  const contact = useSiteContent("contact", {});
  const address = lang === "tr" ? contact.address_tr : contact.address_en;
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/messages", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="contact-page" className="pt-40 lg:pt-44 bg-[#F6F8FA]">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="overline text-[#4A6A8F]">{t.contact.eyebrow}</div>
        <h1 className="font-serif-ap text-5xl sm:text-6xl lg:text-7xl text-[#0F2746] mt-6 max-w-3xl leading-[1.02] tracking-tight">
          {t.contact.title}
        </h1>
        <p className="text-[#3A5170] mt-8 max-w-xl">{t.contact.subtitle}</p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="border-t border-[#0F2746]/20 pt-8">
            <div className="overline text-[#4A6A8F] mb-3">Adres</div>
            <p className="font-serif-ap text-2xl text-[#0F2746] whitespace-pre-line">{address || "Levent Mah.\nİstanbul, Türkiye"}</p>
          </div>
          <div className="border-t border-[#0F2746]/20 pt-8 mt-12">
            <div className="overline text-[#4A6A8F] mb-3">{t.contact.email}</div>
            <a href={`mailto:${contact.email || "contact@alacapartners.com"}`} className="font-serif-ap text-2xl text-[#0F2746] ap-link-underline">{contact.email || "contact@alacapartners.com"}</a>
          </div>
          <div className="border-t border-[#0F2746]/20 pt-8 mt-12">
            <div className="overline text-[#4A6A8F] mb-3">{t.contact.phone}</div>
            <p className="font-serif-ap text-2xl text-[#0F2746]">{contact.phone || "+90 (212) 000 00 00"}</p>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label={t.contact.name} name="name" value={form.name} onChange={onChange("name")} required />
            <Field label={t.contact.email} name="email" type="email" value={form.email} onChange={onChange("email")} required />
            <Field label={t.contact.phone} name="phone" value={form.phone} onChange={onChange("phone")} />
            <Field label={t.contact.subject} name="subject" value={form.subject} onChange={onChange("subject")} />
          </div>
          <Field label={t.contact.message} name="message" textarea value={form.message} onChange={onChange("message")} required />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="bg-[#0F2746] text-[#F3F5F8] hover:bg-[#081A33] disabled:opacity-50 px-10 py-4 text-sm tracking-[0.2em] uppercase font-semibold transition-colors"
          >
            {loading ? "..." : t.contact.submit}
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, textarea }) {
  return (
    <label className="block">
      <span className="overline text-[10px] text-[#4A6A8F] block mb-3">{label}{required && " *"}</span>
      {textarea ? (
        <textarea
          data-testid={`field-${name}`}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={6}
          className="w-full bg-transparent border-b border-[#0F2746]/30 focus:border-[#0F2746] outline-none py-3 text-[#0F2746] resize-none transition-colors"
        />
      ) : (
        <input
          data-testid={`field-${name}`}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className="w-full bg-transparent border-b border-[#0F2746]/30 focus:border-[#0F2746] outline-none py-3 text-[#0F2746] transition-colors"
        />
      )}
    </label>
  );
}
